/* Created by Interactive 63 - https://interactive63.com */
/**
 * ID prefixing for exports: gives every `id` in an SVG a scene-specific prefix and updates every
 * reference to it, so two exported SVGs on one page never clash. Done by hand rather than with a
 * general optimiser so the behaviour is predictable.
 */

const URL_REFERENCE = /url\(\s*(['"]?)#([^)'"\s]+)\1\s*\)/g;

/** Rewrites `url(#id)` references for ids in the map. */
function rewriteUrls(value: string, rename: (id: string) => string | undefined): string {
  return value.replace(URL_REFERENCE, (match, quote: string, id: string) => {
    const renamed = rename(id);
    return renamed === undefined ? match : `url(${quote}#${renamed}${quote})`;
  });
}

/**
 * Prefixes the ids under an element and every reference to them. An id that already carries the
 * prefix is left alone, so running this twice on the same markup does nothing the second time.
 */
export function prefixIds(root: Element, prefix: string): void {
  const ids = new Set<string>();
  for (const el of [root, ...Array.from(root.querySelectorAll('[id]'))]) {
    const id = el.getAttribute('id');
    if (id) ids.add(id);
  }
  /** The new name for an id, or undefined when it should stay as it is. */
  const rename = (id: string): string | undefined =>
    ids.has(id) && !id.startsWith(prefix) ? `${prefix}${id}` : undefined;

  for (const el of [root, ...Array.from(root.querySelectorAll('*'))]) {
    for (const attr of Array.from(el.attributes)) {
      const name = attr.name;
      const value = attr.value;
      if (name === 'id') {
        const renamed = rename(value);
        if (renamed !== undefined) el.setAttribute('id', renamed);
      } else if (name === 'href' || name === 'xlink:href') {
        if (value.startsWith('#')) {
          const renamed = rename(value.slice(1));
          if (renamed !== undefined) el.setAttribute(name, `#${renamed}`);
        }
      } else if (name === 'aria-labelledby' || name === 'aria-describedby') {
        const next = value
          .split(/\s+/)
          .filter((token) => token !== '')
          .map((token) => rename(token) ?? token)
          .join(' ');
        if (next !== value) el.setAttribute(name, next);
      } else if (value.includes('url(')) {
        const next = rewriteUrls(value, rename);
        if (next !== value) el.setAttribute(name, next);
      }
    }
    if (el.localName === 'style' && el.textContent) {
      const next = rewriteUrls(el.textContent, rename);
      if (next !== el.textContent) el.textContent = next;
    }
  }
}
